import { useMemo } from "react";
import { useTranslation } from "@/packages/utils";
import type { AuditAction } from "./use-audit-status-change";
import { VALID_TRANSITIONS_KEYS } from "./constants";

interface ValidTransitionConfig {
  action: AuditAction;
  label: string;
}

/**
 * Hook that returns translated audit transition labels.
 * Replaces the hardcoded VALID_TRANSITIONS labels with i18n values.
 */
export function useAuditTransitionLabels() {
  const { t } = useTranslation(["audits"]);

  return useMemo(() => {
    const config: Record<string, ValidTransitionConfig> = {};

    // Get all labels upfront to satisfy TypeScript
    const transitionLabels = {
      pending: t("audits:transition.pending"),
      in_progress: t("audits:transition.in_progress"),
      submitted: t("audits:transition.submitted"),
      approved: t("audits:transition.approved"),
      rejected: t("audits:transition.rejected"),
      archived: t("audits:transition.archived"),
    };

    const rollbackLabels = {
      pending: t("audits:transition.rollback.pending"),
      in_progress: t("audits:transition.rollback.in_progress"),
      submitted: t("audits:transition.rollback.submitted"),
    };

    // Map transitions to label keys
    const transitionMap: Record<string, keyof typeof transitionLabels> = {
      "pending->in_progress": "pending",
      "in_progress->submitted": "in_progress",
      "submitted->approved": "submitted",
      "approved->archived": "approved",
      "rejected->submitted": "rejected",
      "archived->in_progress": "archived",
    };

    // Map rollback transitions to rollback label keys
    const rollbackMap: Record<string, keyof typeof rollbackLabels> = {
      "in_progress->pending": "pending",
      "submitted->in_progress": "in_progress",
      "approved->submitted": "submitted",
    };

    for (const [key, actionConfig] of Object.entries(VALID_TRANSITIONS_KEYS)) {
      const transitionKey = transitionMap[key];
      const rollbackKey = rollbackMap[key];

      let label: string;
      if (transitionKey) {
        // Forward transition — use transition key
        label = transitionLabels[transitionKey] || "";
      } else if (rollbackKey) {
        // Backward transition — use rollback key
        label = rollbackLabels[rollbackKey] || "";
      } else {
        // Fallback for unmapped transitions
        label = "";
      }

      config[key] = {
        action: actionConfig.action,
        label,
      };
    }

    return config;
  }, [t]);
}
